import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Spinner } from 'react-bootstrap';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, RefreshCw } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../../api/client';
import '../../assets/styles/LandMapLayerBuilder.css';
import '../../assets/styles/LandMapReportPanel.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const LAND_PARCEL_FIELDS_TO_HIDE = new Set([
  'land_parcel_guid',
  'site_id',
  'polygon',
]);

const FIELD_LABELS = {
  land_parcel_number: 'Parcel Name',
  locality: 'Locality',
  area_in_ha: 'Area (ha)',
  crop_type: 'Crop type',
  notes: 'Notes',
};

const SIDE_PANEL_DEFAULT_WIDTH = 380;
const SIDE_PANEL_MIN_WIDTH = 360;
const SIDE_PANEL_MAX_WIDTH = 920;
const REPORT_PANEL_SESSION_STATE_KEY = 'land-map-report-panel-state';
const reportPdfObjectUrlCache = { siteId: '', objectUrl: '' };

const getSidePanelMaxWidth = () => {
  if (typeof window === 'undefined') return SIDE_PANEL_MAX_WIDTH;

  return Math.max(
    SIDE_PANEL_MIN_WIDTH,
    Math.min(SIDE_PANEL_MAX_WIDTH, Math.floor(window.innerWidth * 0.5))
  );
};

const formatDate = (dateValue) => {
  if (!dateValue) return 'Unavailable';

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) return dateValue;

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Shows one flight date when start and end match, otherwise shows the full acquisition range.
const formatMissionDateRange = (mission) => {
  const startDate = formatDate(mission.acquired_at_start);
  const endDate = formatDate(mission.acquired_at_end);

  if (startDate === endDate || endDate === 'Unavailable') return startDate;
  if (startDate === 'Unavailable') return endDate;

  return `${startDate} - ${endDate}`;
};

const formatFieldLabel = (fieldName) => (
  FIELD_LABELS[fieldName] || fieldName
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase())
);

const formatFieldValue = (fieldName, value) => {
  if (fieldName === 'area_in_ha') {
    const parsedValue = Number(value);
    return Number.isNaN(parsedValue) ? value : parsedValue.toFixed(2);
  }

  return value;
};

const getReportMetadataFromResponse = (responseData) => {
  if (!responseData) return null;

  if (typeof responseData.available === 'boolean') {
    return responseData.available ? responseData.report : null;
  }

  return responseData;
};

const getStoredReportPanelState = (siteId) => {
  if (!siteId || typeof window === 'undefined') return null;

  try {
    const storedState = JSON.parse(window.sessionStorage.getItem(REPORT_PANEL_SESSION_STATE_KEY) || 'null');
    if (storedState?.siteId !== siteId) return null;

    const pageNumber = Number(storedState.pageNumber);

    return {
      activePanelTab: storedState.activePanelTab === 'report' ? 'report' : 'details',
      pageNumber: Number.isInteger(pageNumber) && pageNumber > 0 ? pageNumber : 1,
    };
  } catch {
    return null;
  }
};

const saveReportPanelState = (siteId, panelState) => {
  if (!siteId || typeof window === 'undefined') return;

  window.sessionStorage.setItem(REPORT_PANEL_SESSION_STATE_KEY, JSON.stringify({
    siteId,
    activePanelTab: panelState.activePanelTab === 'report' ? 'report' : 'details',
    pageNumber: panelState.pageNumber,
  }));
};

function LandParcelInfoPanel({
  selectedProject,
  selectedSite,
  selectedLandParcel,
  imageryCategories,
  selectedImageryCategory,
  setSelectedImageryCategory,
  selectedMissionId,
  setSelectedMissionId,
  selectedTileLayers,
  selectedTileLayerKeys,
  selectedElevationLayer,
  maxSelectedTileLayers,
  maxSelectedElevationLayers,
  onAddImageryLayer,
  onAddAllMissionLayers,
  onRemoveSelectedTileLayer,
  onRemoveSelectedElevationLayer,
  onClearSelectedImageryLayers,
  getImageryLayerKey,
  isElevationImageryLayer,
  isTileImageryLayer,
  getSelectedLayerTitle,
  loadingImageryCategories,
  imageryCategoryError,
  imageryData,
  loadingImageryData,
  imageryDataError,
  imageryLayerLabel = {},
}) {
  const reportViewerRef = useRef(null);
  const selectedSiteId = selectedSite?.site_id || '';
  const initialReportPanelState = getStoredReportPanelState(selectedSiteId);
  const reportPanelStateSiteIdRef = useRef(selectedSiteId);
  const [sidePanelWidth, setSidePanelWidth] = useState(SIDE_PANEL_DEFAULT_WIDTH);
  const [activePanelTab, setActivePanelTab] = useState(initialReportPanelState?.activePanelTab || 'details');
  const [reportMetadata, setReportMetadata] = useState(null);
  const [loadingReportMetadata, setLoadingReportMetadata] = useState(false);
  const [reportMetadataChecked, setReportMetadataChecked] = useState(false);
  const [reportMetadataError, setReportMetadataError] = useState(null);
  const [reportPdfUrl, setReportPdfUrl] = useState('');
  const [loadingReportPdf, setLoadingReportPdf] = useState(false);
  const [reportPdfError, setReportPdfError] = useState(null);
  const [reportPdfRequestKey, setReportPdfRequestKey] = useState(0);
  const [reportPageNumber, setReportPageNumber] = useState(initialReportPanelState?.pageNumber || 1);
  const [reportPageCount, setReportPageCount] = useState(0);
  const [reportPageInputValue, setReportPageInputValue] = useState(String(initialReportPanelState?.pageNumber || 1));
  const [reportViewerWidth, setReportViewerWidth] = useState(0);

  const filteredMissions = useMemo(() => {
    const missions = imageryData?.acquisition_missions || [];

    if (!selectedImageryCategory) {
      return [];
    }

    return missions.filter((mission) => mission.source_type === selectedImageryCategory);
  }, [imageryData, selectedImageryCategory]);

  const selectedMission = useMemo(() => (
    filteredMissions.find((mission) => mission.mission_id === selectedMissionId) || null
  ), [filteredMissions, selectedMissionId]);

  const selectedMissionLayers = selectedMission?.imagery_layers || [];
  const selectedMissionHasNoLayers = selectedMission && !selectedMissionLayers.length;
  const selectedElevationLayerKey = selectedElevationLayer?.key || '';
  const selectedLayerCount = selectedTileLayers.length + (selectedElevationLayer ? 1 : 0);

  const addableTileLayers = useMemo(() => (
    selectedMissionLayers.filter((imageryLayer) => (
      imageryLayer?.agol_url &&
      isTileImageryLayer(imageryLayer) &&
      !selectedTileLayerKeys.has(getImageryLayerKey(selectedMission, imageryLayer))
    ))
  ), [getImageryLayerKey, isTileImageryLayer, selectedMission, selectedMissionLayers, selectedTileLayerKeys]);

  const addableElevationLayer = useMemo(() => (
    selectedMissionLayers.find((imageryLayer) => (
      imageryLayer?.agol_url && isElevationImageryLayer(imageryLayer)
    )) || null
  ), [isElevationImageryLayer, selectedMissionLayers]);

  const nonNullLandParcelEntries = useMemo(() => {
    if (!selectedLandParcel) return [];

    // Only show land parcel data that has a real value and is useful to read in the panel.
    return Object.entries(selectedLandParcel).filter(([fieldName, value]) => (
      !LAND_PARCEL_FIELDS_TO_HIDE.has(fieldName) &&
      value !== null &&
      value !== undefined &&
      value !== ''
    ));
  }, [selectedLandParcel]);

  const handleResizeStart = useCallback((event) => {
    event.preventDefault();

    const startX = event.clientX;
    const startWidth = sidePanelWidth;

    const handleMouseMove = (moveEvent) => {
      const deltaX = startX - moveEvent.clientX;
      const nextWidth = Math.min(
        getSidePanelMaxWidth(),
        Math.max(SIDE_PANEL_MIN_WIDTH, startWidth + deltaX)
      );

      setSidePanelWidth(nextWidth);
    };

    const handleMouseUp = () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('land-map-panel-resizing');
    };

    document.body.classList.add('land-map-panel-resizing');
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  }, [sidePanelWidth]);

  const resetSidePanelWidth = () => {
    setSidePanelWidth(Math.min(SIDE_PANEL_DEFAULT_WIDTH, getSidePanelMaxWidth()));
  };

  useEffect(() => {
    const clampPanelWidth = () => {
      setSidePanelWidth((currentWidth) => Math.min(currentWidth, getSidePanelMaxWidth()));
    };

    clampPanelWidth();
    window.addEventListener('resize', clampPanelWidth);

    return () => window.removeEventListener('resize', clampPanelWidth);
  }, []);

  useEffect(() => {
    if (reportPanelStateSiteIdRef.current !== selectedSiteId) return;

    saveReportPanelState(selectedSiteId, {
      activePanelTab,
      pageNumber: reportPageNumber,
    });
  }, [activePanelTab, reportPageNumber, selectedSiteId]);

  useEffect(() => {
    let isMounted = true;
    const storedReportPanelState = getStoredReportPanelState(selectedSiteId);
    const initialPageNumber = storedReportPanelState?.pageNumber || 1;

    reportPanelStateSiteIdRef.current = selectedSiteId;
    setReportMetadata(null);
    setReportMetadataError(null);
    setReportMetadataChecked(false);
    setReportPdfError(null);
    setReportPdfRequestKey(0);
    setReportPageNumber(initialPageNumber);
    setReportPageCount(0);
    setReportPageInputValue(String(initialPageNumber));
    setActivePanelTab(storedReportPanelState?.activePanelTab || 'details');

    if (!selectedSiteId) {
      setLoadingReportMetadata(false);
      setReportMetadataChecked(true);
      return undefined;
    }

    const fetchReportMetadata = async () => {
      setLoadingReportMetadata(true);

      try {
        const response = await api.get(`/api/v1/land-management/sites/${selectedSiteId}/farm-assessment-report/metadata`);
        if (isMounted) {
          setReportMetadata(getReportMetadataFromResponse(response.data));
        }
      } catch (requestError) {
        if (isMounted) {
          setReportMetadata(null);

          if (requestError.response?.status !== 404) {
            setReportMetadataError(requestError);
          }
        }
      } finally {
        if (isMounted) {
          setLoadingReportMetadata(false);
          setReportMetadataChecked(true);
        }
      }
    };

    fetchReportMetadata();

    return () => {
      isMounted = false;
    };
  }, [selectedSiteId]);

  useEffect(() => {
    if (reportMetadataChecked && !reportMetadata && activePanelTab === 'report') {
      setActivePanelTab('details');
    }
  }, [activePanelTab, reportMetadataChecked, reportMetadata]);

  useEffect(() => {
    if (!selectedSiteId || !reportMetadata) {
      setReportPdfUrl('');
      return undefined;
    }

    const cachedReportPdfUrl = reportPdfObjectUrlCache.siteId === selectedSiteId
      ? reportPdfObjectUrlCache.objectUrl
      : '';

    if (cachedReportPdfUrl && reportPdfRequestKey === 0) {
      setReportPdfError(null);
      setLoadingReportPdf(false);
      setReportPdfUrl(cachedReportPdfUrl);
      return undefined;
    }

    let isMounted = true;

    const fetchReportPdf = async () => {
      setLoadingReportPdf(true);
      setReportPdfError(null);
      setReportPdfUrl('');

      if (reportPdfObjectUrlCache.objectUrl) {
        URL.revokeObjectURL(reportPdfObjectUrlCache.objectUrl);
        reportPdfObjectUrlCache.siteId = '';
        reportPdfObjectUrlCache.objectUrl = '';
      }

      try {
        const response = await api.get(`/api/v1/land-management/sites/${selectedSiteId}/farm-assessment-report/pdf`, {
          responseType: 'blob',
        });

        const objectUrl = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        reportPdfObjectUrlCache.siteId = selectedSiteId;
        reportPdfObjectUrlCache.objectUrl = objectUrl;

        if (isMounted) {
          setReportPdfUrl(objectUrl);
        }
      } catch (requestError) {
        if (isMounted) {
          setReportPdfError(requestError);
        }
      } finally {
        if (isMounted) {
          setLoadingReportPdf(false);
        }
      }
    };

    fetchReportPdf();

    return () => {
      isMounted = false;
    };
  }, [selectedSiteId, reportMetadata, reportPdfRequestKey]);

  useEffect(() => {
    const viewerElement = reportViewerRef.current;
    if (!viewerElement) return undefined;

    const getViewerContentWidth = () => {
      const viewerStyles = window.getComputedStyle(viewerElement);
      const horizontalPadding = parseFloat(viewerStyles.paddingLeft || '0') + parseFloat(viewerStyles.paddingRight || '0');
      return Math.max(0, Math.floor(viewerElement.clientWidth - horizontalPadding));
    };

    const updateViewerWidth = () => {
      setReportViewerWidth(getViewerContentWidth());
    };

    updateViewerWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateViewerWidth);
      return () => window.removeEventListener('resize', updateViewerWidth);
    }

    const resizeObserver = new ResizeObserver(() => {
      setReportViewerWidth(getViewerContentWidth());
    });

    resizeObserver.observe(viewerElement);

    return () => resizeObserver.disconnect();
  }, [activePanelTab, reportPdfUrl]);

  useEffect(() => {
    setReportPageInputValue(String(reportPageNumber));
  }, [reportPageNumber]);

  const formatImageryCategoryLabel = (category) => {
    if (!category) return '';

    const normalisedCategory = category.toLowerCase();

    if (normalisedCategory === 'lidar') return 'LiDAR';

    return category.charAt(0).toUpperCase() + category.slice(1);
  };

  const getAvailableLayerTitle = (imageryLayer) => (
    imageryLayerLabel[imageryLayer.layer_type] || imageryLayer.platform_title || imageryLayer.agol_title || 'Imagery layer'
  );

  const canAddAllMissionLayers = selectedMission && (
    (addableTileLayers.length > 0 && selectedTileLayers.length < maxSelectedTileLayers) ||
    (!selectedElevationLayer && addableElevationLayer)
  );

  const handleReportLoaded = ({ numPages }) => {
    setReportPageCount(numPages);
    setReportPageNumber((currentPage) => Math.min(currentPage, numPages));
  };

  const goToReportPage = (nextPage) => {
    if (!reportPageCount) return;
    setReportPageNumber(Math.min(reportPageCount, Math.max(1, nextPage)));
  };

  const commitReportPageInput = () => {
    const parsedPageNumber = Number(reportPageInputValue);

    if (!Number.isInteger(parsedPageNumber) || parsedPageNumber < 1) {
      setReportPageInputValue(String(reportPageNumber));
      return;
    }

    goToReportPage(parsedPageNumber);
  };

  const handleReportPageInputChange = (event) => {
    const nextValue = event.target.value;

    if (/^\d*$/.test(nextValue)) {
      setReportPageInputValue(nextValue);
    }
  };

  const handleReportPageInputKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.currentTarget.blur();
      commitReportPageInput();
    }
  };

  const handleRetryReport = () => {
    setReportPdfError(null);
    setReportPdfUrl('');
    setReportPdfRequestKey((currentKey) => currentKey + 1);
  };

  const handleDownloadReport = () => {
    if (!reportPdfUrl) return;

    const downloadLink = document.createElement('a');
    downloadLink.href = reportPdfUrl;
    downloadLink.download = reportMetadata?.filename || 'x10AI-FarmTwin-Farm-Assessment-Report.pdf';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  return (
    <aside className="land-map-side-panel" style={{ width: sidePanelWidth }}>
      <button
        aria-label="Drag to resize details panel"
        className="land-map-panel-resize-handle"
        onDoubleClick={resetSidePanelWidth}
        onMouseDown={handleResizeStart}
        title="Drag to resize. Double-click to reset."
        type="button"
      >
        <span aria-hidden="true" />
      </button>

      <div className="land-map-side-panel-content">
        <section className="land-map-panel-section land-map-panel-summary">
          <div>
            <p className="land-map-panel-eyebrow">Selected Site</p>
            <h3>{selectedSite?.site_name || 'No site selected'}</h3>
          </div>
          {selectedProject && (
            <Badge bg="success" className="land-map-project-badge">
              {selectedProject.project_name}
            </Badge>
          )}
        </section>

        <div className="land-map-panel-tabs" role="tablist" aria-label="Land parcel panel views">
          <Button
            className="land-map-panel-tab-button"
            size="sm"
            variant={activePanelTab === 'details' ? 'success' : 'outline-success'}
            onClick={() => setActivePanelTab('details')}
          >
            Overview & Imagery
          </Button>
          {reportMetadata && (
            <Button
              className="land-map-panel-tab-button"
              size="sm"
              variant={activePanelTab === 'report' ? 'success' : 'outline-success'}
              onClick={() => setActivePanelTab('report')}
            >
              <span>Farm Assessment Report</span>
              {loadingReportPdf && <Spinner animation="border" size="sm" />}
            </Button>
          )}
          {loadingReportMetadata && <Spinner animation="border" size="sm" />}
        </div>

        {reportMetadataError && (
          <section className="land-map-panel-section">
            <Alert variant="warning" className="land-map-panel-alert">
              Report availability could not be checked.
            </Alert>
          </section>
        )}

        <div className="land-map-details-tab-panel" hidden={activePanelTab !== 'details'}>
          <section className="land-map-panel-section land-map-parcel-section">
            <div className="land-map-panel-section-heading">
              <h4>Land Parcel Data</h4>
            </div>

            {!selectedLandParcel && (
              <p className="land-map-empty-text">Select a land parcel from the dropdown or map.</p>
            )}

            {!!nonNullLandParcelEntries.length && (
              <dl className="land-map-parcel-data-list">
                {nonNullLandParcelEntries.map(([fieldName, value]) => (
                  <div key={fieldName} className="land-map-parcel-data-row">
                    <dt>{formatFieldLabel(fieldName)}</dt>
                    <dd>{formatFieldValue(fieldName, value)}</dd>
                  </div>
                ))}
              </dl>
            )}
          </section>

          <section className="land-map-panel-section land-map-category-section">
            <div className="land-map-panel-section-heading">
              <h4>Imagery Categories</h4>
              {loadingImageryCategories && <Spinner animation="border" size="sm" />}
            </div>

            {!selectedSite && (
              <p className="land-map-empty-text">Select a site to view imagery categories.</p>
            )}

            {selectedSite && imageryCategoryError && (
              <Alert variant="warning" className="land-map-panel-alert">
                Imagery categories could not be loaded.
              </Alert>
            )}

            {selectedSite && !loadingImageryCategories && !imageryCategories.length && !imageryCategoryError && (
              <p className="land-map-empty-text">No imagery categories are available for this site.</p>
            )}

            {!!imageryCategories.length && (
              <div className="land-map-category-buttons">
                {imageryCategories.map((category) => (
                  <Button
                    active={selectedImageryCategory === category}
                    key={`${selectedSite?.site_id || 'no-site'}-${category}`}
                    size="sm"
                    variant={selectedImageryCategory === category ? 'success' : 'outline-success'}
                    onClick={(event) => {
                      // Clear the pressed/focused visual state so category styling cannot linger after a site change.
                      event.currentTarget.blur();
                      setSelectedImageryCategory(category);
                    }}
                  >
                    {formatImageryCategoryLabel(category)}
                  </Button>
                ))}
              </div>
            )}
          </section>

          <section className="land-map-panel-section land-map-flight-section">
            <div className="land-map-panel-section-heading">
              <h4>Imagery Acquisition Dates</h4>
              {loadingImageryData && <Spinner animation="border" size="sm" />}
            </div>

            {!selectedSite && (
              <p className="land-map-empty-text">Select a site to view imagery acquisition dates.</p>
            )}

            {selectedSite && imageryDataError && (
              <p className="land-map-empty-text">Imagery missions could not be loaded or are not available.</p>
            )}

            {selectedSite && selectedImageryCategory && !loadingImageryData && !filteredMissions.length && !imageryDataError && (
              <p className="land-map-empty-text">No imagery acquisition dates are available for this category.</p>
            )}

            {selectedSite && !selectedImageryCategory && !loadingImageryData && !imageryDataError && (
              <p className="land-map-empty-text">Select an imagery category to view available dates.</p>
            )}

            {!!filteredMissions.length && (
              <div className="land-map-flight-list">
                {filteredMissions.map((mission) => {
                  const isSelectedMission = selectedMissionId === mission.mission_id;

                  return (
                    <button
                      aria-pressed={isSelectedMission}
                      className="land-map-flight-item"
                      key={mission.mission_id}
                      onClick={() => setSelectedMissionId(mission.mission_id)}
                      style={isSelectedMission ? { backgroundColor: '#edf7e9', borderColor: '#4ea72e' } : undefined}
                      type="button"
                    >
                      <div>
                        <strong>{formatMissionDateRange(mission)}</strong>
                      </div>
                      <Badge bg="light" text="dark">
                        {mission.imagery_layers?.length || 0} layers
                      </Badge>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="land-map-panel-section land-map-layer-builder-section">
            <div className="land-map-panel-section-heading">
              <div>
                <h4>Available Layers</h4>
                <span className="land-map-layer-count-text">{selectedTileLayers.length} / {maxSelectedTileLayers} tile layers selected</span>
                <span className="land-map-layer-count-text">{selectedElevationLayer ? 1 : 0} / {maxSelectedElevationLayers} elevation layers selected</span>
              </div>
              <Button
                size="sm"
                variant="outline-success"
                disabled={!canAddAllMissionLayers}
                onClick={() => onAddAllMissionLayers(selectedMission)}
              >
                Add all
              </Button>
            </div>

            {!selectedMission && !loadingImageryData && !imageryDataError && (
              <p className="land-map-empty-text">Select an imagery acquisition date to view available layers.</p>
            )}

            {selectedSite && imageryDataError && (
              <p className="land-map-empty-text land-map-layer-empty-text">
                No imagery layers are available for this acquisition date.
              </p>
            )}

            {selectedElevationLayer && selectedMissionLayers.some((imageryLayer) => {
              const layerKey = getImageryLayerKey(selectedMission, imageryLayer);
              return isElevationImageryLayer(imageryLayer) && selectedElevationLayerKey !== layerKey;
            }) && (
              <p className="land-map-empty-text land-map-layer-empty-text">
                Remove current DSM to add another.
              </p>
            )}

            {selectedMissionHasNoLayers && (
              <p className="land-map-empty-text land-map-layer-empty-text">
                No imagery layers are available for this acquisition date.
              </p>
            )}

            {!!selectedMissionLayers.length && (
              <div className="land-map-layer-list land-map-available-layer-list">
                {selectedMissionLayers.map((imageryLayer) => {
                  const layerKey = getImageryLayerKey(selectedMission, imageryLayer);
                  const isElevationLayer = isElevationImageryLayer(imageryLayer);
                  const isTileLayer = isTileImageryLayer(imageryLayer);
                  const isSelectedTile = selectedTileLayerKeys.has(layerKey);
                  const isSelectedElevation = selectedElevationLayerKey === layerKey;
                  const isDuplicate = isSelectedTile || isSelectedElevation;
                  const isBlockedByElevation = isElevationLayer && !!selectedElevationLayer && !isSelectedElevation;
                  const isBlockedByTileLimit = isTileLayer && selectedTileLayers.length >= maxSelectedTileLayers && !isSelectedTile;
                  const cannotAdd = isDuplicate || isBlockedByElevation || isBlockedByTileLimit || (!isTileLayer && !isElevationLayer);
                  const availableLayerTitle = getAvailableLayerTitle(imageryLayer);

                  return (
                    <div className="land-map-layer-row" key={layerKey}>
                      <div className="land-map-layer-row-main">
                        <strong title={availableLayerTitle}>{availableLayerTitle}</strong>
                        <div className="land-map-layer-badges">
                          {imageryLayer.agol_layer_type && <Badge bg="secondary">{imageryLayer.agol_layer_type}</Badge>}
                        </div>
                      </div>
                      <Button
                        className="land-map-layer-action-button"
                        size="sm"
                        variant={isDuplicate ? 'outline-secondary' : 'success'}
                        disabled={cannotAdd}
                        onClick={() => onAddImageryLayer(selectedMission, imageryLayer)}
                      >
                        {isDuplicate ? 'Added' : 'Add'}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="land-map-panel-section land-map-selected-layer-section">
            <div className="land-map-panel-section-heading">
              <div>
                <h4>Selected Map Layers</h4>
                <span className="land-map-layer-count-text">{selectedLayerCount} active selections</span>
              </div>
              <Button
                size="sm"
                variant="outline-danger"
                disabled={!selectedLayerCount}
                onClick={onClearSelectedImageryLayers}
              >
                Clear all
              </Button>
            </div>

            {!selectedLayerCount && (
              <p className="land-map-empty-text">Add layers from an acquisition date to show them on the map.</p>
            )}

            {!!selectedLayerCount && (
              <div className="land-map-layer-list">
                {selectedTileLayers.map((selectedLayer) => {
                  const selectedLayerTitle = getSelectedLayerTitle(selectedLayer, true);

                  return (
                    <div className="land-map-layer-row" key={selectedLayer.key}>
                      <div className="land-map-layer-row-main">
                        <strong title={selectedLayerTitle}>{selectedLayerTitle}</strong>
                        <div className="land-map-layer-badges">
                          {selectedLayer.agol_layer_type && <Badge bg="secondary">{selectedLayer.agol_layer_type}</Badge>}
                        </div>
                      </div>
                      <Button
                        className="land-map-layer-action-button"
                        size="sm"
                        variant="outline-danger"
                        onClick={() => onRemoveSelectedTileLayer(selectedLayer.key)}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })}

                {selectedElevationLayer && (() => {
                  const selectedElevationTitle = getSelectedLayerTitle(selectedElevationLayer, true);

                  return (
                    <div className="land-map-layer-row land-map-elevation-layer-row">
                      <div className="land-map-layer-row-main">
                        <strong title={selectedElevationTitle}>{selectedElevationTitle}</strong>
                        <div className="land-map-layer-badges">
                          {selectedElevationLayer.agol_layer_type && <Badge bg="secondary">{selectedElevationLayer.agol_layer_type}</Badge>}
                        </div>
                      </div>
                      <Button
                        className="land-map-layer-action-button"
                        size="sm"
                        variant="outline-danger"
                        onClick={onRemoveSelectedElevationLayer}
                      >
                        Remove
                      </Button>
                    </div>
                  );
                })()}
              </div>
            )}
          </section>
        </div>

        {reportMetadata && (
          <section className="land-map-panel-section land-map-report-section" hidden={activePanelTab !== 'report'}>
            {loadingReportPdf && (
              <div className="land-map-report-loading">
                <Spinner animation="border" role="status" />
                <span>Loading report...</span>
              </div>
            )}

            {reportPdfError && (
              <Alert variant="warning" className="land-map-panel-alert land-map-report-error">
                <span>The report could not be loaded. Please try again.</span>
                <Button
                  className="land-map-report-retry-button"
                  onClick={handleRetryReport}
                  size="sm"
                  variant="outline-warning"
                >
                  <RefreshCw aria-hidden="true" size={16} />
                  <span>Retry</span>
                </Button>
              </Alert>
            )}

            {reportPdfUrl && !reportPdfError && (
              <>
                <div className="land-map-report-toolbar">
                  <div className="land-map-report-title">
                    <strong>{reportMetadata.title || 'x10AI FarmTwin Farm Assessment Report'}</strong>
                    {reportMetadata.created_at && <span>Generated {formatDate(reportMetadata.created_at)}</span>}
                  </div>
                  <Button
                    className="land-map-report-download-button"
                    onClick={handleDownloadReport}
                    size="sm"
                    title="Download report"
                    variant="outline-success"
                  >
                    <Download aria-hidden="true" size={16} />
                    <span>Download</span>
                  </Button>
                </div>

                <div className="land-map-report-viewer" ref={reportViewerRef}>
                  <Document
                    file={reportPdfUrl}
                    loading={<Spinner animation="border" role="status" />}
                    onLoadSuccess={handleReportLoaded}
                  >
                    <Page
                      pageNumber={reportPageNumber}
                      renderAnnotationLayer={false}
                      renderTextLayer={false}
                      width={reportViewerWidth || undefined}
                    />
                  </Document>
                </div>

                <div className="land-map-report-controls">
                  <div className="land-map-report-page-actions">
                    <Button
                      disabled={reportPageNumber <= 1}
                      onClick={() => goToReportPage(1)}
                      size="sm"
                      title="First page"
                      variant="outline-success"
                    >
                      <ChevronsLeft aria-hidden="true" size={16} />
                    </Button>
                    <Button
                      disabled={reportPageNumber <= 1}
                      onClick={() => goToReportPage(reportPageNumber - 1)}
                      size="sm"
                      title="Previous page"
                      variant="outline-success"
                    >
                      <ChevronLeft aria-hidden="true" size={16} />
                    </Button>
                  </div>
                  <label className="land-map-report-page-input-label">
                    <span>Page</span>
                    <input
                      aria-label="Report page number"
                      className="land-map-report-page-input"
                      disabled={!reportPageCount}
                      inputMode="numeric"
                      max={reportPageCount || undefined}
                      min="1"
                      onBlur={commitReportPageInput}
                      onChange={handleReportPageInputChange}
                      onKeyDown={handleReportPageInputKeyDown}
                      pattern="[0-9]*"
                      type="text"
                      value={reportPageInputValue}
                    />
                    <span>/ {reportPageCount || '-'}</span>
                  </label>
                  <div className="land-map-report-page-actions">
                    <Button
                      disabled={!reportPageCount || reportPageNumber >= reportPageCount}
                      onClick={() => goToReportPage(reportPageNumber + 1)}
                      size="sm"
                      title="Next page"
                      variant="outline-success"
                    >
                      <ChevronRight aria-hidden="true" size={16} />
                    </Button>
                    <Button
                      disabled={!reportPageCount || reportPageNumber >= reportPageCount}
                      onClick={() => goToReportPage(reportPageCount)}
                      size="sm"
                      title="Last page"
                      variant="outline-success"
                    >
                      <ChevronsRight aria-hidden="true" size={16} />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </section>
        )}
      </div>
    </aside>
  );
}

export default LandParcelInfoPanel;